/**
 * Responsive utility functions for consistent breakpoint handling
 */

// Tailwind CSS breakpoints (matching tailwind.config.js)
export const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
}

/**
 * Get current breakpoint based on window width
 * @param {number} width - Window width
 * @returns {string} Current breakpoint name
 */
export const getCurrentBreakpoint = (width) => {
  if (width >= BREAKPOINTS['2xl']) return '2xl'
  if (width >= BREAKPOINTS.xl) return 'xl'
  if (width >= BREAKPOINTS.lg) return 'lg'
  if (width >= BREAKPOINTS.md) return 'md'
  if (width >= BREAKPOINTS.sm) return 'sm'
  return 'xs'
}

/**
 * Check if current width is mobile
 * @param {number} width - Window width
 * @returns {boolean} True if mobile
 */
export const isMobileWidth = (width) => width < BREAKPOINTS.md

/**
 * Check if current width is tablet
 * @param {number} width - Window width
 * @returns {boolean} True if tablet
 */
export const isTabletWidth = (width) => 
  width >= BREAKPOINTS.md && width < BREAKPOINTS.lg

/**
 * Check if current width is desktop
 * @param {number} width - Window width
 * @returns {boolean} True if desktop
 */
export const isDesktopWidth = (width) => width >= BREAKPOINTS.lg

/**
 * Get responsive class names based on breakpoint
 * @param {Object} classes - Object with breakpoint keys and class values
 * @param {string} currentBreakpoint - Current breakpoint
 * @returns {string} Appropriate class names
 */
export const getResponsiveClasses = (classes, currentBreakpoint) => {
  const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl', '2xl']
  const currentIndex = breakpointOrder.indexOf(currentBreakpoint)
  
  // Find the most appropriate class for current breakpoint
  for (let i = currentIndex; i >= 0; i--) {
    const breakpoint = breakpointOrder[i]
    if (classes[breakpoint]) {
      return classes[breakpoint]
    }
  }
  
  // Fallback to base class
  return classes.base || ''
}

/**
 * Generate responsive grid columns based on breakpoint
 * @param {Object} config - Grid configuration per breakpoint
 * @param {string} currentBreakpoint - Current breakpoint
 * @returns {string} Grid column classes
 */
export const getResponsiveGridCols = (config, currentBreakpoint) => {
  const defaultConfig = {
    xs: 1,
    sm: 1,
    md: 2,
    lg: 3,
    xl: 4,
    '2xl': 4,
    ...config
  }
  
  const cols = defaultConfig[currentBreakpoint] || defaultConfig.xs
  return `grid-cols-${cols}`
}

/**
 * Get responsive spacing classes
 * @param {Object} spacing - Spacing configuration per breakpoint
 * @param {string} currentBreakpoint - Current breakpoint
 * @returns {string} Spacing classes
 */
export const getResponsiveSpacing = (spacing, currentBreakpoint) => {
  const defaultSpacing = {
    xs: 'p-4 space-y-4',
    sm: 'p-4 space-y-4',
    md: 'p-6 space-y-6',
    lg: 'p-8 space-y-8',
    xl: 'p-8 space-y-8',
    '2xl': 'p-8 space-y-8',
    ...spacing
  }
  
  return defaultSpacing[currentBreakpoint] || defaultSpacing.xs
}

/**
 * Get responsive text size classes
 * @param {Object} textSizes - Text size configuration per breakpoint
 * @param {string} currentBreakpoint - Current breakpoint
 * @returns {string} Text size classes
 */
export const getResponsiveTextSize = (textSizes, currentBreakpoint) => {
  const defaultTextSizes = {
    xs: 'text-sm',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-base',
    xl: 'text-lg',
    '2xl': 'text-lg',
    ...textSizes
  }
  
  return defaultTextSizes[currentBreakpoint] || defaultTextSizes.xs
}

/**
 * Calculate optimal sidebar width based on screen size
 * @param {number} screenWidth - Current screen width
 * @returns {Object} Sidebar configuration
 */
export const getSidebarConfig = (screenWidth) => {
  if (screenWidth < BREAKPOINTS.md) {
    return {
      type: 'overlay',
      width: '280px',
      collapsible: true,
      defaultOpen: false
    }
  } else if (screenWidth < BREAKPOINTS.lg) {
    return {
      type: 'overlay',
      width: '280px',
      collapsible: true,
      defaultOpen: false
    }
  } else {
    return {
      type: 'fixed',
      width: '256px',
      collapsible: false,
      defaultOpen: true
    }
  }
}

/**
 * Get touch-friendly sizing for interactive elements
 * @param {string} size - Base size (sm, md, lg)
 * @param {boolean} isMobile - Whether current device is mobile
 * @returns {string} Appropriate size classes
 */
export const getTouchFriendlySize = (size, isMobile) => {
  const sizes = {
    sm: isMobile ? 'h-11 px-4 text-sm' : 'h-9 px-3 text-sm',
    md: isMobile ? 'h-12 px-6 text-base' : 'h-10 px-4 text-sm',
    lg: isMobile ? 'h-14 px-8 text-lg' : 'h-11 px-6 text-base'
  }
  
  return sizes[size] || sizes.md
}

/**
 * Generate responsive table configuration
 * @param {Array} columns - All available columns
 * @param {string} currentBreakpoint - Current breakpoint
 * @returns {Object} Table configuration
 */
export const getResponsiveTableConfig = (columns, currentBreakpoint) => {
  const config = {
    xs: {
      visibleColumns: columns.slice(0, 2), // Show only 2 most important columns
      layout: 'card', // Use card layout instead of table
      showActions: true
    },
    sm: {
      visibleColumns: columns.slice(0, 3), // Show 3 columns
      layout: 'table',
      showActions: true
    },
    md: {
      visibleColumns: columns.slice(0, 4), // Show 4 columns
      layout: 'table',
      showActions: true
    },
    lg: {
      visibleColumns: columns, // Show all columns
      layout: 'table',
      showActions: true
    },
    xl: {
      visibleColumns: columns, // Show all columns
      layout: 'table',
      showActions: true
    },
    '2xl': {
      visibleColumns: columns, // Show all columns
      layout: 'table',
      showActions: true
    }
  }
  
  return config[currentBreakpoint] || config.xs
}